import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";

const ThemeModeContext = createContext({ mode: "light", toggle: () => {} });

// Thunder AI design tokens
const TOKENS = {
  primary: "#6C50BF",          // indigo / violet
  primaryRgb: "108, 80, 191",
  primaryDark: "#492ba3",
  primaryLight: "#BABDFD",
  primaryLighter: "#EDE8FA",    // subtle bg

  success: "#39c645",
  danger: "#ff1f35",
  warning: "#f9bc06",
  info: "#4178E5",

  lightPink: "#F4EDFA",
  lightGreen: "#EDFAF2",
  lightBlue: "#EDF3FA",
  lightOrange: "#FAEDED",
  lightGray: "#F9F9FA",
};

function buildTheme(mode) {
  const isDark = mode === "dark";

  const palette = isDark
    ? {
        mode: "dark",
        primary: { main: TOKENS.primary, light: TOKENS.primaryLight, dark: TOKENS.primaryDark, lighter: "#2a1f52", contrastText: "#FFFFFF" },
        secondary: { main: "#53bdb1", light: "#8dd3ca", dark: "#2fa294", contrastText: "#FFFFFF" },
        success: { main: TOKENS.success, contrastText: "#FFFFFF" },
        error: { main: TOKENS.danger, contrastText: "#FFFFFF" },
        warning: { main: TOKENS.warning, contrastText: "#000000" },
        info: { main: TOKENS.info, contrastText: "#FFFFFF" },
        background: { default: "#000000", paper: "#121212" },
        text: { primary: "#FFFFFF", secondary: "rgba(255,255,255,0.7)", disabled: "rgba(255,255,255,0.4)" },
        divider: "rgba(255,255,255,0.12)",
        action: { hover: "rgba(108, 80, 191, 0.12)", selected: "rgba(108, 80, 191, 0.20)" },
      }
    : {
        mode: "light",
        primary: { main: TOKENS.primary, light: TOKENS.primaryLight, dark: TOKENS.primaryDark, lighter: TOKENS.primaryLighter, contrastText: "#FFFFFF" },
        secondary: { main: "#53bdb1", light: "#8dd3ca", dark: "#2fa294", contrastText: "#FFFFFF" },
        success: { main: TOKENS.success, contrastText: "#FFFFFF" },
        error: { main: TOKENS.danger, contrastText: "#FFFFFF" },
        warning: { main: TOKENS.warning, contrastText: "#000000" },
        info: { main: TOKENS.info, contrastText: "#FFFFFF" },
        background: { default: "#FFFFFF", paper: "#F7F7F8" },
        // Secondary text was rgba(0,0,0,0.6) ≈ #999 which computed to 2.65:1
        // against our F7F7F8 paper bg — below WCAG AA 4.5 for body text. Bump
        // to 0.72 opacity (≈ #474747) so captions stay readable on all
        // card/paper backgrounds.
        text: { primary: "#111111", secondary: "rgba(0,0,0,0.72)", disabled: "rgba(0,0,0,0.38)" },
        divider: "#eeeeee",
        action: { hover: "rgba(108, 80, 191, 0.06)", selected: "rgba(108, 80, 191, 0.12)" },
      };

  const accent = {
    pink: TOKENS.lightPink,
    green: TOKENS.lightGreen,
    blue: TOKENS.lightBlue,
    orange: TOKENS.lightOrange,
    gray: TOKENS.lightGray,
  };

  return createTheme({
    palette,
    accent,
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"Raleway", "Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      fontSize: 14,
      h1: { fontWeight: 700, fontSize: "2.25rem", lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: "1.875rem", lineHeight: 1.3 },
      h3: { fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.3 },
      h4: { fontWeight: 700, fontSize: "1.25rem", lineHeight: 1.4 },
      h5: { fontWeight: 600, fontSize: "1.125rem", lineHeight: 1.4 },
      h6: { fontWeight: 600, fontSize: "1rem", lineHeight: 1.5 },
      subtitle1: { fontWeight: 600, fontSize: "0.95rem" },
      subtitle2: { fontWeight: 600, fontSize: "0.85rem" },
      body1: { fontSize: "0.9rem", lineHeight: 1.6 },
      body2: { fontSize: "0.8rem", lineHeight: 1.6 },
      caption: { fontSize: "0.75rem" },
      overline: { fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    },
    shadows: [
      "none",
      "0 1px 2px rgba(0,0,0,0.04)",
      "0 2px 4px rgba(0,0,0,0.04)",
      "0 4px 8px rgba(0,0,0,0.06)",
      "0 6px 12px rgba(0,0,0,0.08)",
      "0 8px 16px rgba(0,0,0,0.08)",
      ...Array(19).fill("0 12px 24px rgba(0,0,0,0.10)"),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: '"Raleway", "Open Sans", sans-serif',
            scrollbarWidth: "thin",
            scrollbarColor: isDark ? "#404040 #1a1a1a" : "#cccccc #f5f5f5",
          },
          "*::-webkit-scrollbar": { width: 6, height: 6 },
          "*::-webkit-scrollbar-track": { background: isDark ? "#1a1a1a" : "#f5f5f5" },
          "*::-webkit-scrollbar-thumb": {
            background: isDark ? "#404040" : "#cccccc",
            borderRadius: 3,
          },
          "*::-webkit-scrollbar-thumb:hover": {
            background: isDark ? "#606060" : "#999999",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, fontWeight: 600, padding: "8px 20px" },
          sizeSmall: { padding: "4px 12px", fontSize: "0.8rem" },
          sizeLarge: { padding: "12px 28px", fontSize: "0.95rem" },
          containedPrimary: {
            background: `linear-gradient(135deg, ${TOKENS.primary} 0%, ${TOKENS.primaryDark} 100%)`,
            "&:hover": {
              background: `linear-gradient(135deg, ${TOKENS.primaryDark} 0%, ${TOKENS.primary} 100%)`,
              boxShadow: `0 4px 12px rgba(${TOKENS.primaryRgb},0.3)`,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundColor: isDark ? "#121212" : "#F7F7F8",
            backgroundImage: "none",
            border: isDark ? "1px solid #262626" : "1px solid #eeeeee",
            transition: "all 0.25s ease",
          },
        },
      },
      // Cards are the dominant surface on every screen; their 24px content
      // padding is comfortable on desktop but makes phones scroll a lot. Tighten
      // to 16px on mobile so more content fits above the fold without feeling
      // cramped. Desktop is unchanged.
      MuiCardContent: {
        styleOverrides: {
          root: {
            "@media (max-width:600px)": { padding: 16, "&:last-child": { paddingBottom: 16 } },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 12,
          },
          elevation1: {
            boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.5)" : "0 2px 8px rgba(0,0,0,0.05)",
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? "#000000" : "#FFFFFF",
            borderBottom: isDark ? "1px solid #262626" : "1px solid #eeeeee",
            backgroundImage: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? "#000000" : "#FFFFFF",
            backgroundImage: "none",
            borderRight: isDark ? "1px solid #262626" : "1px solid #eeeeee",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isDark ? "1px solid #262626" : "1px solid #eeeeee",
          },
          head: {
            fontWeight: 700,
            backgroundColor: isDark ? "#121212" : "#F7F7F8",
            color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            textTransform: "uppercase",
            fontSize: "0.72rem",
            letterSpacing: "0.04em",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: "0.72rem" },
          sizeSmall: { height: 22 },
          colorPrimary: { backgroundColor: TOKENS.primaryLighter, color: TOKENS.primary },
          // Secondary was falling back to MUI's teal #53bdb1 + white text, which
          // measures ~2.26:1 against white — below WCAG AA 4.5. Match the soft
          // colored-bg + dark-text pattern used by Primary/Success/Error/Info
          // chips (axe-core color-contrast violation).
          colorSecondary: { backgroundColor: "#D9F0ED", color: "#0E5E54" },
          colorSuccess: { backgroundColor: TOKENS.lightGreen, color: "#1a7f27" },
          colorError: { backgroundColor: TOKENS.lightOrange, color: "#b30010" },
          colorWarning: { backgroundColor: "#FFF8E1", color: "#8a6600" },
          colorInfo: { backgroundColor: TOKENS.lightBlue, color: "#1e4ea8" },
        },
      },
      MuiTextField: { defaultProps: { variant: "outlined", size: "small" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark ? "#121212" : "#FFFFFF",
            "& fieldset": { borderColor: isDark ? "#262626" : "#eeeeee" },
            "&:hover fieldset": { borderColor: isDark ? "#404040" : "#cccccc" },
            "&.Mui-focused fieldset": { borderColor: TOKENS.primary, borderWidth: 2 },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            "&.Mui-selected": {
              backgroundColor: TOKENS.primaryLighter,
              color: TOKENS.primary,
              "&:hover": { backgroundColor: TOKENS.primaryLighter },
              "& .MuiListItemIcon-root": { color: TOKENS.primary },
            },
          },
        },
      },
      // Tab strips default to fixed-width layout which silently clips tabs
      // that overflow the viewport on mobile (≤375px) — e.g. the "My Points"
      // tab on /kudos and "Expiring" on /my-documents were unreachable on
      // phones. Scrollable + allowScrollButtonsMobile keeps every tab
      // reachable at any width while remaining unchanged on desktop.
      MuiTabs: {
        defaultProps: {
          variant: "scrollable",
          scrollButtons: "auto",
          allowScrollButtonsMobile: true,
        },
      },
      MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600, minHeight: 44 } } },
      MuiToolbar: { styleOverrides: { root: { minHeight: "64px !important" } } },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 700 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            // On phones the default 32px margin leaves dialogs at ~83% width with
            // wasted gutters and cramped form fields. Use nearly the full width and
            // allow the dialog to grow tall with internal scroll. Desktop unchanged.
            "@media (max-width:600px)": {
              margin: 12,
              width: "calc(100% - 24px)",
              maxWidth: "calc(100% - 24px)",
              maxHeight: "calc(100% - 24px)",
              borderRadius: 14,
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 700, padding: "20px 24px", "@media (max-width:600px)": { padding: "16px 16px" } },
        },
      },
      MuiDialogContent: {
        styleOverrides: { root: { "@media (max-width:600px)": { padding: "8px 16px" } } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { "@media (max-width:600px)": { padding: "8px 12px 12px" } } },
      },
      // Meet the 44px minimum touch target on phones without changing the tighter
      // desktop density (icon buttons are visually ~20px but need a larger hit area).
      MuiIconButton: {
        styleOverrides: { root: { "@media (max-width:600px)": { minWidth: 44, minHeight: 44 } } },
      },
    },
  });
}

export function AppThemeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem("themeMode") || "light");

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
    document.documentElement.setAttribute("data-bs-theme", mode);
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const ctx = useMemo(
    () => ({ mode, toggle: () => setMode((m) => (m === "light" ? "dark" : "light")) }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
